import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router';
import { validateInstallment, type InstallmentDraft } from '@tahi/core';
import {
  addInstallment,
  deleteInstallment,
  editInstallment,
  restoreInstallment,
} from '../../../data/commands/payments';
import { useCommandEnv } from '../../../data/hooks';
import { Button, Sheet, showToast } from '../../../ui';
import { TextField } from '../../../ui/TextField';
import { useCategories, useGoBack } from '../../spending/hooks';
import { INPUT } from '../../spending/styles';
import { copy, errorText } from '../copy';
import { minorToInput, parsePesoInput, peso } from '../format';
import { PAYMENTS_BASE, usePaymentsSnapshot, useToday } from '../hooks';

/** Add or edit a card installment (/p/:id/installments/new, /p/:id/installments/:iid). */
export function InstallmentRoute() {
  const { id = '', iid } = useParams();
  const env = useCommandEnv();
  const today = useToday();
  const snap = usePaymentsSnapshot();
  const categories = useCategories() ?? [];
  const goBack = useGoBack(`${PAYMENTS_BASE}/p/${id}`);
  const existing = iid ? snap?.installments.find((i) => i.id === iid) : undefined;
  const [item, setItem] = useState(existing?.item ?? '');
  const [monthly, setMonthly] = useState(existing ? minorToInput(existing.monthlyMinor) : '');
  const [months, setMonths] = useState(existing ? String(existing.months) : '12');
  const [start, setStart] = useState(existing?.startMonth ?? today.slice(0, 7));
  const [categoryId, setCategoryId] = useState(existing?.categoryId ?? '');
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(Boolean(existing) || !iid);

  // The row may arrive after the first render (deep link): fill the form once it does.
  if (!loaded && existing) {
    setLoaded(true);
    setItem(existing.item);
    setMonthly(minorToInput(existing.monthlyMinor));
    setMonths(String(existing.months));
    setStart(existing.startMonth);
    setCategoryId(existing.categoryId ?? '');
  }

  const close = (open: boolean) => !open && goBack();
  if (!snap) return null;

  const draft = (): InstallmentDraft => ({
    item,
    monthlyMinor: parsePesoInput(monthly) ?? 0,
    months: /^\d+$/.test(months) ? Number(months) : 0,
    startMonth: start,
    categoryId: categoryId || null,
    note: existing?.note ?? null,
  });
  const d = draft();

  async function submit(e: FormEvent) {
    e.preventDefault();
    const err = validateInstallment(d);
    if (err) return setError(errorText(err));
    const r = existing
      ? await editInstallment(env, existing.id, d)
      : await addInstallment(env, id, d);
    if (!r.ok) return setError(errorText(r.error));
    showToast({ message: copy.installmentSaved });
    goBack();
  }

  async function remove() {
    if (!existing) return;
    await deleteInstallment(env, existing.id);
    showToast({
      message: copy.installmentDeleted,
      action: { label: copy.undo, onPress: () => void restoreInstallment(env, existing.id) },
    });
    goBack();
  }

  return (
    <Sheet open onOpenChange={close} title={existing ? existing.item : copy.addInstallment}>
      <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-3" noValidate>
        <TextField
          label="Item"
          placeholder="Phone"
          value={item}
          onChange={(e) => setItem(e.target.value)}
        />
        <div className="grid grid-cols-2 gap-3">
          <TextField
            label="Monthly amount"
            inputMode="decimal"
            autoComplete="off"
            value={monthly}
            onChange={(e) => setMonthly(e.target.value)}
          />
          <TextField
            label="Months"
            inputMode="numeric"
            autoComplete="off"
            value={months}
            onChange={(e) => setMonths(e.target.value)}
          />
        </div>
        <TextField
          label="First month"
          hint="The month of the first charge on your statement"
          type="month"
          value={start}
          onChange={(e) => e.target.value && setStart(e.target.value)}
        />
        <label className="flex flex-col gap-1 text-sm font-medium text-muted">
          Category
          <select
            className={INPUT}
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">None</option>
            {categories
              .filter((c) => !c.hidden)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
        </label>
        {d.monthlyMinor > 0 && d.months > 0 ? (
          <p className="text-sm text-muted">
            Total {peso(d.monthlyMinor * d.months)}. Each month counts as spending on its own, never
            the full price.
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <Button type="submit" variant="primary" block>
          {copy.save}
        </Button>
        {existing ? (
          <Button variant="quiet-destructive" block onClick={() => void remove()}>
            {copy.delete}
          </Button>
        ) : null}
      </form>
    </Sheet>
  );
}
